// src/components/UpdateProductForm.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

const UpdateProductForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const existingProduct = location.state || {};

  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // product form state
  const [product, setProduct] = useState({
    id: "",
    title: "",
    description: "",
    price: "",
    quantity: 0,
    categoryId: "",
    supplierId: "",
    purchasePrice: "",
  });

  // image files + previews (4)
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [file3, setFile3] = useState(null);
  const [file4, setFile4] = useState(null);

  const [preview1, setPreview1] = useState(null);
  const [preview2, setPreview2] = useState(null);
  const [preview3, setPreview3] = useState(null);
  const [preview4, setPreview4] = useState(null);

  useEffect(() => {
    // init product from existingProduct (safe defaults)
    if (existingProduct && existingProduct.id) {
      setProduct({
        id: existingProduct.id ?? "",
        title: existingProduct.title ?? "",
        description: existingProduct.description ?? "",
        price: existingProduct.price ?? "",
        quantity: 0,
        categoryId: existingProduct.category?.id ?? "",
        supplierId: existingProduct.supplier?.id ?? "",
        purchasePrice: existingProduct.purchasePrice ?? "",
      });

      // existing images (from server) as previews
      if (existingProduct.imageName) {
        setPreview1(
          `https://goldenhides.in/api/product/${existingProduct.imageName}`
        );
      }
      if (existingProduct.imageName2) {
        setPreview2(
          `https://goldenhides.in/api/product/${existingProduct.imageName2}`
        );
      }
      if (existingProduct.imageName3) {
        setPreview3(
          `https://goldenhides.in/api/product/${existingProduct.imageName3}`
        );
      }
      if (existingProduct.imageName4) {
        setPreview4(
          `https://goldenhides.in/api/product/${existingProduct.imageName4}`
        );
      }
    }
  }, [existingProduct]);

  useEffect(() => {
    // fetch categories and suppliers
    const fetchMeta = async () => {
      try {
        const [catResp, supResp] = await Promise.all([
          axios.get("https://goldenhides.in/api/category/all"),
          axios.get("https://goldenhides.in/api/user/supplier/all"),
        ]);
        setCategories(catResp.data?.categories || []);
        setSuppliers(supResp.data?.users || []);
      } catch (err) {
        console.error("Failed to load meta", err);
        toast.error("Failed to load categories or suppliers", {
          position: "top-center",
        });
      }
    };
    fetchMeta();
  }, []);

  // input handlers
  const handleInput = (e) => {
    const { name, value } = e.target;
    setProduct((p) => ({ ...p, [name]: value }));
  };

  const handleFile = (fileSetter, previewSetter) => (e) => {
    const f = e.target.files?.[0] ?? null;
    fileSetter(f);
    if (!f) {
      // if removed, we leave existing preview (server image) or null
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => previewSetter(ev.target.result);
    reader.readAsDataURL(f);
  };

  const validate = () => {
    if (!product.title || !product.title.trim()) {
      toast.error("Please enter product title", { position: "top-center" });
      return false;
    }
    if (!product.description || !product.description.trim()) {
      toast.error("Please enter product description", {
        position: "top-center",
      });
      return false;
    }
    if (!product.categoryId) {
      toast.error("Please select a category", { position: "top-center" });
      return false;
    }
    if (!product.supplierId) {
      toast.error("Please select a supplier", { position: "top-center" });
      return false;
    }
    if (product.price === "" || isNaN(Number(product.price))) {
      toast.error("Enter a valid selling price", { position: "top-center" });
      return false;
    }
    if (product.purchasePrice === "" || isNaN(Number(product.purchasePrice))) {
      toast.error("Enter a valid purchase price", { position: "top-center" });
      return false;
    }
    return true;
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const formData = new FormData();
    if (file1) formData.append("image", file1);
    if (file2) formData.append("image2", file2);
    if (file3) formData.append("image3", file3);
    if (file4) formData.append("image4", file4);

    formData.append("id", product.id);
    formData.append("title", product.title);
    formData.append("description", product.description);
    formData.append("price", String(product.price));
    formData.append("quantity", String(product.quantity || 0));
    formData.append("categoryId", String(product.categoryId));
    formData.append("supplierId", String(product.supplierId));
    formData.append("purchasePrice", String(product.purchasePrice));

    try {
      const resp = await axios.post(
        "https://goldenhides.in/api/product/update",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const body = resp.data;
      if (body.success) {
        toast.success(body.responseMessage || "Product updated", {
          position: "top-center",
        });
        setTimeout(() => navigate("/home"), 1200);
      } else {
        toast.error(body.responseMessage || "Update failed", {
          position: "top-center",
        });
      }
    } catch (err) {
      console.error("Update error", err);
      toast.error("Server error while updating product", {
        position: "top-center",
      });
    }
  };

  return (
    <div className="container my-4">
      <div className="card shadow-sm" style={{ borderRadius: 10 }}>
        <div
          className="card-header bg-color custom-bg-text d-flex justify-content-between align-items-center"
          style={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}
        >
          <div>
            <h4 className="mb-0">Update Product</h4>
            <small className="text-muted">
              Edit product details and images
            </small>
          </div>
        </div>

        <div className="card-body">
          <form onSubmit={saveProduct}>
            <div className="row">
              {/* Images column */}
              <div className="col-md-4 mb-3">
                <div className="mb-3">
                  <label className="form-label">
                    <strong>Image 1</strong>
                  </label>
                  <div
                    className="mb-2"
                    style={{
                      width: "100%",
                      height: 180,
                      border: "1px solid #eee",
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    {preview1 ? (
                      <img
                        src={preview1}
                        alt="img1"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div className="text-muted">No image</div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={handleFile(setFile1, setPreview1)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Image 2</strong>
                  </label>
                  <div
                    className="mb-2"
                    style={{
                      width: "100%",
                      height: 120,
                      border: "1px solid #eee",
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    {preview2 ? (
                      <img
                        src={preview2}
                        alt="img2"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div className="text-muted">No image</div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={handleFile(setFile2, setPreview2)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Image 3</strong>
                  </label>
                  <div
                    className="mb-2"
                    style={{
                      width: "100%",
                      height: 120,
                      border: "1px solid #eee",
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    {preview3 ? (
                      <img
                        src={preview3}
                        alt="img3"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div className="text-muted">No image</div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={handleFile(setFile3, setPreview3)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <strong>Image 4</strong>
                  </label>
                  <div
                    className="mb-2"
                    style={{
                      width: "100%",
                      height: 120,
                      border: "1px solid #eee",
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    {preview4 ? (
                      <img
                        src={preview4}
                        alt="img4"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <div className="text-muted">No image</div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={handleFile(setFile4, setPreview4)}
                  />
                </div>
              </div>

              {/* Fields column */}
              <div className="col-md-8">
                <div className="row">
                  <div className="col-12 mb-3">
                    <label className="form-label">
                      <strong>Title</strong>
                    </label>
                    <input
                      name="title"
                      value={product.title}
                      onChange={handleInput}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="col-12 mb-3">
                    <label className="form-label">
                      <strong>Description</strong>
                    </label>
                    <textarea
                      name="description"
                      value={product.description}
                      onChange={handleInput}
                      rows={4}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <strong>Category</strong>
                    </label>
                    <select
                      name="categoryId"
                      value={product.categoryId}
                      onChange={handleInput}
                      className="form-control"
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <strong>Supplier</strong>
                    </label>
                    <select
                      name="supplierId"
                      value={product.supplierId}
                      onChange={handleInput}
                      className="form-control"
                      required
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.firstName} {s.lastName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <strong>Update Quantity</strong>
                    </label>
                    <input
                      name="quantity"
                      type="number"
                      min="0"
                      value={product.quantity}
                      onChange={handleInput}
                      className="form-control"
                    />
                    <small className="text-muted">
                      Enter amount to add or set. Leave 0 to keep existing.
                    </small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <strong>Available (current)</strong>
                    </label>
                    <input
                      className="form-control"
                      value={existingProduct.quantity ?? 0}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <strong>Supplied Quantity</strong>
                    </label>
                    <input
                      className="form-control"
                      value={existingProduct.suppliedQuantity ?? 0}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <strong>Selling Price (₹)</strong>
                    </label>
                    <input
                      name="price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={product.price}
                      onChange={handleInput}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <strong>Purchase Price (₹)</strong>
                    </label>
                    <input
                      name="purchasePrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={product.purchasePrice}
                      onChange={handleInput}
                      className="form-control"
                      required
                    />
                  </div>
                </div>

                <div className="d-flex gap-2 justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn bg-color custom-bg-text">
                    Update Product
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default UpdateProductForm;
